/**
 * /tools/six-step-reframing?clientId=X — STP-036.
 *
 * Step 5 — the dragon work. 6 ordered steps; each submission creates a new
 * `reframing_sessions` row (clients can run the protocol multiple times).
 * No hydration — each session is fresh because each engagement targets a
 * different pattern.
 *
 * Closes WFL-123 Affirmations → Dragon Work cross-flow leg.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface FormState {
  patternDescription: string;
  blockedVirtueName: string;
  triggerSituation: string;
  partName: string;
  bodyLocation: string;
  surfaceIntention: string;
  deeperIntention: string;
  coreIntention: string;
  alternative1: string;
  alternative2: string;
  alternative3: string;
  weeklyCommitment: string;
  confidenceScore: string;
  objections: string;
  virtueAffirmation: string;
  ifThenPlan: string;
}

const DEFAULT_FORM: FormState = {
  patternDescription: '',
  blockedVirtueName: '',
  triggerSituation: '',
  partName: '',
  bodyLocation: '',
  surfaceIntention: '',
  deeperIntention: '',
  coreIntention: '',
  alternative1: '',
  alternative2: '',
  alternative3: '',
  weeklyCommitment: '',
  confidenceScore: '',
  objections: '',
  virtueAffirmation: '',
  ifThenPlan: '',
};

export function SixStepReframingPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const canSubmit = !submitting && form.patternDescription.trim().length > 0;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.sixStepReframing.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const optionalString = (s: string): string | undefined => {
      const v = s.trim();
      return v.length > 0 ? v : undefined;
    };
    const conf = form.confidenceScore.trim();
    const confNum = conf ? Number(conf) : NaN;
    const payload = {
      patternDescription: form.patternDescription.trim(),
      ...(optionalString(form.blockedVirtueName) !== undefined
        ? { blockedVirtueName: optionalString(form.blockedVirtueName) }
        : {}),
      ...(optionalString(form.triggerSituation) !== undefined
        ? { triggerSituation: optionalString(form.triggerSituation) }
        : {}),
      ...(optionalString(form.partName) !== undefined
        ? { partName: optionalString(form.partName) }
        : {}),
      ...(optionalString(form.bodyLocation) !== undefined
        ? { bodyLocation: optionalString(form.bodyLocation) }
        : {}),
      ...(optionalString(form.surfaceIntention) !== undefined
        ? { surfaceIntention: optionalString(form.surfaceIntention) }
        : {}),
      ...(optionalString(form.deeperIntention) !== undefined
        ? { deeperIntention: optionalString(form.deeperIntention) }
        : {}),
      ...(optionalString(form.coreIntention) !== undefined
        ? { coreIntention: optionalString(form.coreIntention) }
        : {}),
      ...(optionalString(form.alternative1) !== undefined
        ? { alternative1: optionalString(form.alternative1) }
        : {}),
      ...(optionalString(form.alternative2) !== undefined
        ? { alternative2: optionalString(form.alternative2) }
        : {}),
      ...(optionalString(form.alternative3) !== undefined
        ? { alternative3: optionalString(form.alternative3) }
        : {}),
      ...(optionalString(form.weeklyCommitment) !== undefined
        ? { weeklyCommitment: optionalString(form.weeklyCommitment) }
        : {}),
      ...(Number.isInteger(confNum) && confNum >= 1 && confNum <= 10
        ? { confidenceScore: confNum }
        : {}),
      ...(optionalString(form.objections) !== undefined
        ? { objections: optionalString(form.objections) }
        : {}),
      ...(optionalString(form.virtueAffirmation) !== undefined
        ? { virtueAffirmation: optionalString(form.virtueAffirmation) }
        : {}),
      ...(optionalString(form.ifThenPlan) !== undefined
        ? { ifThenPlan: optionalString(form.ifThenPlan) }
        : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'six-step-reframing',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
      setForm(DEFAULT_FORM);
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.sixStepReframing.errors.generic'));
      } else {
        setSubmitError(t('tools.sixStepReframing.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="six-step-reframing-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.sixStepReframing.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.sixStepReframing.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.sixStepReframing.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.sixStepReframing.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.sixStepReframing.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <Step n={1} titleKey="tools.sixStepReframing.step1.title" hintKey="tools.sixStepReframing.step1.hint">
          <Field labelKey="tools.sixStepReframing.fields.patternDescription">
            <textarea
              value={form.patternDescription}
              onChange={set('patternDescription')}
              placeholder={t('tools.sixStepReframing.placeholders.patternDescription')}
              className="input-field min-h-24 w-full"
              data-testid="six-step-reframing-pattern"
              rows={3}
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.blockedVirtue" optional>
            <input
              type="text"
              value={form.blockedVirtueName}
              onChange={set('blockedVirtueName')}
              placeholder={t('tools.sixStepReframing.placeholders.blockedVirtue')}
              className="input-field w-full"
              data-testid="six-step-reframing-blocked-virtue"
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.trigger" optional>
            <input
              type="text"
              value={form.triggerSituation}
              onChange={set('triggerSituation')}
              placeholder={t('tools.sixStepReframing.placeholders.trigger')}
              className="input-field w-full"
              data-testid="six-step-reframing-trigger"
            />
          </Field>
        </Step>

        <Step n={2} titleKey="tools.sixStepReframing.step2.title" hintKey="tools.sixStepReframing.step2.hint">
          <Field labelKey="tools.sixStepReframing.fields.partName" optional>
            <input
              type="text"
              value={form.partName}
              onChange={set('partName')}
              placeholder={t('tools.sixStepReframing.placeholders.partName')}
              className="input-field w-full"
              data-testid="six-step-reframing-part-name"
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.bodyLocation" optional>
            <input
              type="text"
              value={form.bodyLocation}
              onChange={set('bodyLocation')}
              placeholder={t('tools.sixStepReframing.placeholders.bodyLocation')}
              className="input-field w-full"
              data-testid="six-step-reframing-body-location"
            />
          </Field>
        </Step>

        <Step n={3} titleKey="tools.sixStepReframing.step3.title" hintKey="tools.sixStepReframing.step3.hint">
          <Field labelKey="tools.sixStepReframing.fields.surfaceIntention" optional>
            <input
              type="text"
              value={form.surfaceIntention}
              onChange={set('surfaceIntention')}
              placeholder={t('tools.sixStepReframing.placeholders.surfaceIntention')}
              className="input-field w-full"
              data-testid="six-step-reframing-surface-intention"
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.deeperIntention" optional>
            <input
              type="text"
              value={form.deeperIntention}
              onChange={set('deeperIntention')}
              placeholder={t('tools.sixStepReframing.placeholders.deeperIntention')}
              className="input-field w-full"
              data-testid="six-step-reframing-deeper-intention"
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.coreIntention" optional>
            <textarea
              value={form.coreIntention}
              onChange={set('coreIntention')}
              placeholder={t('tools.sixStepReframing.placeholders.coreIntention')}
              className="input-field min-h-20 w-full"
              data-testid="six-step-reframing-core-intention"
              rows={3}
            />
          </Field>
        </Step>

        <Step n={4} titleKey="tools.sixStepReframing.step4.title" hintKey="tools.sixStepReframing.step4.hint">
          <Field labelKey="tools.sixStepReframing.fields.alternative1" optional>
            <input
              type="text"
              value={form.alternative1}
              onChange={set('alternative1')}
              placeholder={t('tools.sixStepReframing.placeholders.alternative1')}
              className="input-field w-full"
              data-testid="six-step-reframing-alt-1"
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.alternative2" optional>
            <input
              type="text"
              value={form.alternative2}
              onChange={set('alternative2')}
              placeholder={t('tools.sixStepReframing.placeholders.alternative2')}
              className="input-field w-full"
              data-testid="six-step-reframing-alt-2"
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.alternative3" optional>
            <input
              type="text"
              value={form.alternative3}
              onChange={set('alternative3')}
              placeholder={t('tools.sixStepReframing.placeholders.alternative3')}
              className="input-field w-full"
              data-testid="six-step-reframing-alt-3"
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.weeklyCommitment" optional>
            <input
              type="text"
              value={form.weeklyCommitment}
              onChange={set('weeklyCommitment')}
              placeholder={t('tools.sixStepReframing.placeholders.weeklyCommitment')}
              className="input-field w-full"
              data-testid="six-step-reframing-commitment"
            />
          </Field>
        </Step>

        <Step n={5} titleKey="tools.sixStepReframing.step5.title" hintKey="tools.sixStepReframing.step5.hint">
          <Field labelKey="tools.sixStepReframing.fields.confidence" optional>
            <input
              type="number"
              min={1}
              max={10}
              value={form.confidenceScore}
              onChange={set('confidenceScore')}
              placeholder="1-10"
              className="input-field w-24"
              data-testid="six-step-reframing-confidence"
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.objections" optional>
            <textarea
              value={form.objections}
              onChange={set('objections')}
              placeholder={t('tools.sixStepReframing.placeholders.objections')}
              className="input-field min-h-20 w-full"
              data-testid="six-step-reframing-objections"
              rows={3}
            />
          </Field>
        </Step>

        <Step n={6} titleKey="tools.sixStepReframing.step6.title" hintKey="tools.sixStepReframing.step6.hint">
          <Field labelKey="tools.sixStepReframing.fields.virtueAffirmation" optional>
            <textarea
              value={form.virtueAffirmation}
              onChange={set('virtueAffirmation')}
              placeholder={t('tools.sixStepReframing.placeholders.virtueAffirmation')}
              className="input-field min-h-16 w-full"
              data-testid="six-step-reframing-virtue-affirmation"
              rows={2}
            />
          </Field>
          <Field labelKey="tools.sixStepReframing.fields.ifThenPlan" optional>
            <textarea
              value={form.ifThenPlan}
              onChange={set('ifThenPlan')}
              placeholder={t('tools.sixStepReframing.placeholders.ifThenPlan')}
              className="input-field min-h-20 w-full"
              data-testid="six-step-reframing-if-then"
              rows={3}
            />
          </Field>
        </Step>

        {submitError && (
          <p role="alert" className="form-error" data-testid="six-step-reframing-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p
            role="status"
            className="text-sm text-flame-deep"
            data-testid="six-step-reframing-saved-at"
          >
            ✓ {t('tools.sixStepReframing.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.sixStepReframing.submitting') : t('tools.sixStepReframing.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}

interface StepProps {
  n: number;
  titleKey: string;
  hintKey: string;
  children: React.ReactNode;
}

function Step({ n, titleKey, hintKey, children }: StepProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
      <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
        {t('tools.sixStepReframing.stepLabel', { n })}
      </p>
      <h2 className="font-display text-display-sm text-ink mt-s1">{t(titleKey)}</h2>
      <p className="mt-s2 text-gray-2 italic">{t(hintKey)}</p>
      <div className="mt-s4 space-y-s3">{children}</div>
    </article>
  );
}

interface FieldProps {
  labelKey: string;
  optional?: boolean;
  children: React.ReactNode;
}

function Field({ labelKey, optional, children }: FieldProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <label className="block">
      <span className="form-label">
        {t(labelKey)}
        {optional && (
          <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
            {t('tools.sixStepReframing.optional')}
          </span>
        )}
      </span>
      <div className="mt-s2">{children}</div>
    </label>
  );
}
